import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { useIsMobile } from '@/hooks/useIsMobile';
import { AlertTriangle, HelpCircle } from 'lucide-react';
import { Table, Stat, count } from './ReportTable';

/**
 * What each machine has waiting, and how long it should take.
 *
 * The hours are measured, not standard: every finished run leaves its productive
 * seconds and piece count behind, and the estimate is built from what the press
 * has actually done. Where nothing has been measured the hours are blank rather
 * than zero — an unmeasured machine must not read as an idle one.
 */

const hours = (v) => (v === null || v === undefined ? '—' : `${Number(v).toFixed(1)} h`);

function JobBadges({ job }) {
  return (
    <>
      {job.basis === 'stage' && (
        <Badge className="ml-1 bg-slate-100 text-[9px] text-slate-500">stage avg</Badge>
      )}
      {job.basis !== 'none' && !job.confident && (
        <Badge className="ml-1 bg-amber-50 text-[9px] text-amber-700">1 run</Badge>
      )}
    </>
  );
}

/** A machine as a card that opens to the jobs behind its hours. */
function MachineCard({ row, open, onToggle }) {
  return (
    <li className="rounded-[10px] border border-slate-200 bg-white">
      <button
        type="button"
        data-testid={`machine-row-${row.machine_id}`}
        aria-expanded={open}
        onClick={onToggle}
        className="block w-full px-4 py-3 text-left"
      >
        <span className="flex items-start justify-between gap-3">
          <span className="min-w-0 truncate text-[14px] font-semibold text-slate-900">{row.machine_name}</span>
          <span className="shrink-0 text-[14px] font-semibold tabular-nums text-slate-900">{hours(row.hours)}</span>
        </span>
        <span className="block truncate text-[12px] text-slate-500">
          {row.stages?.length ? row.stages.join(', ') : <span className="text-amber-600">nothing linked</span>}
        </span>
        <span className="mt-1 block text-[12px] tabular-nums text-slate-500">
          {count(row.batches)} batch{row.batches === 1 ? '' : 'es'} · {count(row.quantity)} pcs
          {row.unknown_batches ? <span className="text-amber-600"> · {row.unknown_batches} unmeasured</span> : null}
        </span>
      </button>
      {open && row.jobs?.length > 0 && (
        <ul className="border-t border-slate-100 px-4 py-1">
          {row.jobs.map((job, idx) => (
            <li key={idx} className="flex items-start justify-between gap-3 py-2 text-[12px]">
              <span className="min-w-0">
                <span className="block truncate text-slate-700">{job.order_id} · {job.item_name}</span>
                <span className="block text-[11px] text-slate-500">{job.stage_name} · {count(job.quantity)} pcs</span>
              </span>
              <span className="shrink-0 text-right tabular-nums text-slate-700">
                {hours(job.hours)}
                <JobBadges job={job} />
              </span>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

export default function ProductionSchedule() {
  const isMobile = useIsMobile();
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    api.get('/reports/schedule').then(({ data }) => setData(data))
      .catch(() => toast.error('Could not load the schedule'));
  }, []);

  if (!data) return <p className="py-10 text-center text-sm text-slate-400">Loading…</p>;

  const toggle = (id) => setExpanded(expanded === id ? null : id);

  return (
    <div className="space-y-4" data-testid="production-schedule">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Batches waiting" value={count(data.totals.batches)} />
        <Stat label="Quantity" value={count(data.totals.quantity)} />
        <Stat label="Hours of work" value={hours(data.totals.hours)} />
        <Stat label="Not yet measurable" value={count(data.totals.unknown_batches)}
          tone={data.totals.unknown_batches > 0 ? 'text-amber-600' : undefined} />
      </div>

      {data.totals.unknown_batches > 0 && (
        <p className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 p-2.5 text-xs text-slate-600"
          data-testid="unmeasured-note">
          <HelpCircle className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
          {data.totals.unknown_batches} batch{data.totals.unknown_batches > 1 ? 'es sit' : ' sits'} at a
          process no machine has finished a run on yet, so there is nothing to estimate from. They are
          counted in the queue but not in the hours — the figures fill in as work is scanned through.
        </p>
      )}

      <div>
        <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Machines</p>
        {isMobile ? (
          <ul className="space-y-2" data-testid="machine-load-list">
            {data.machines.length === 0 ? (
              <li className="rounded-[10px] border border-slate-200 bg-white py-10 text-center text-xs text-slate-400">
                No machines on the books yet.
              </li>
            ) : data.machines.map(row => (
              <MachineCard key={row.machine_id} row={row} open={expanded === row.machine_id}
                onToggle={() => toggle(row.machine_id)} />
            ))}
          </ul>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full text-xs" data-testid="machine-load-table">
              <thead className="bg-slate-100 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  {['Machine', 'Runs', 'Batches', 'Quantity', 'Hours', 'Unmeasured'].map(h => (
                    <th key={h} className="whitespace-nowrap px-2.5 py-2 text-left">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.machines.length === 0 ? (
                  <tr><td colSpan={6} className="px-2 py-10 text-center text-slate-400">
                    No machines on the books yet.
                  </td></tr>
                ) : data.machines.map(row => (
                  <React.Fragment key={row.machine_id}>
                    <tr className="cursor-pointer border-t border-slate-200 hover:bg-slate-50"
                      onClick={() => toggle(row.machine_id)}
                      data-testid={`machine-row-${row.machine_id}`}>
                      <td className="px-2.5 py-2 font-medium">{row.machine_name}</td>
                      <td className="px-2.5 py-2 text-slate-500">
                        {row.stages?.length ? row.stages.join(', ') : <span className="text-amber-600">nothing linked</span>}
                      </td>
                      <td className="px-2.5 py-2 text-right tabular-nums">{count(row.batches)}</td>
                      <td className="px-2.5 py-2 text-right tabular-nums">{count(row.quantity)}</td>
                      <td className="px-2.5 py-2 text-right tabular-nums font-medium">{hours(row.hours)}</td>
                      <td className="px-2.5 py-2 text-right tabular-nums text-amber-600">
                        {row.unknown_batches || ''}
                      </td>
                    </tr>
                    {expanded === row.machine_id && row.jobs?.map((job, idx) => (
                      <tr key={idx} className="border-t border-slate-100 bg-slate-50/60 text-[11px]">
                        <td className="px-2.5 py-1.5 pl-6 text-slate-500">{job.order_id} · {job.item_name}</td>
                        <td className="px-2.5 py-1.5 text-slate-500">{job.stage_name}</td>
                        <td className="px-2.5 py-1.5" />
                        <td className="px-2.5 py-1.5 text-right tabular-nums">{count(job.quantity)}</td>
                        <td className="px-2.5 py-1.5 text-right tabular-nums">
                          {hours(job.hours)}
                          <JobBadges job={job} />
                        </td>
                        <td className="px-2.5 py-1.5" />
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {data.unassigned.length > 0 && (
        <div>
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            Booked to no machine
          </p>
          <Table
            tone="warn"
            testId="unassigned-table"
            rows={data.unassigned}
            rowKey={(row) => row.stage_name}
            empty=""
            columns={[
              { key: 'stage', head: 'Process', render: (row) => row.stage_name },
              { key: 'batches', head: 'Batches', align: 'right', render: (row) => count(row.batches) },
              { key: 'quantity', head: 'Quantity', align: 'right', render: (row) => count(row.quantity) },
              { key: 'hours', head: 'Hours', align: 'right', render: (row) => hours(row.hours) },
            ]}
          />
        </div>
      )}

      {data.pre_press.length > 0 && (
        <div>
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            Before the press
          </p>
          <div className="grid gap-2 sm:grid-cols-2" data-testid="pre-press">
            {data.pre_press.map(row => (
              <div key={row.stage_name} className="rounded-lg border border-slate-200 bg-white p-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-slate-800">{row.stage_name}</p>
                  {row.overdue > 0 && (
                    <span className="flex items-center gap-1 text-[11px] text-red-600">
                      <AlertTriangle className="h-3.5 w-3.5" /> {row.overdue} overdue
                    </span>
                  )}
                </div>
                <p className="mt-0.5 text-xs text-slate-500">
                  {count(row.batches)} batch{row.batches === 1 ? '' : 'es'} · {count(row.quantity)} pcs
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
